import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ExternalDataService,
  UnsupportedLocationDto,
  UnsupportedLocationDtoPaginatedResponse,
  Zone,
  UpdateUnsupportedLocationStatusRequestDto,
  GeoZoneLaunchNotificationRequestDto,
  GeoZoneLaunchNotificationJobAcceptedDto,
  WhatsAppCampaignSendLogPaginatedResponse
} from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { PaginationComponent } from '../shared/pagination/pagination.component';

@Component({
  selector: 'app-unsupported-geo-locations-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  templateUrl: './unsupported-geo-locations-list.component.html',
  styleUrl: './unsupported-geo-locations-list.component.css'
})
export class UnsupportedGeoLocationsListComponent implements OnInit, OnDestroy {
  locationsResponse: UnsupportedLocationDtoPaginatedResponse | null = null;
  selectedLocation: UnsupportedLocationDto | null = null;
  showDetailsPanel = false;
  showUpdateStatusModal = false;
  locationForStatusUpdate: UnsupportedLocationDto | null = null;

  private locationsSubscription$ = new Subject<void>();

  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages: number = 1;

  // Filter properties
  filters = {
    geoZoneId: '',
    status: ''
  };

  // Zones
  zones: Zone[] = [];
  loadingZones = false;

  // Update status form
  updateStatusForm = {
    status: '',
    remark: ''
  };

  // Geo-zone launch notification properties
  showGeoZoneLaunchModal = false;
  whatsAppTemplateName = 'location_launch_direct';
  whatsAppLocationParam = '';
  whatsAppHeaderImageUrl = '';
  launchingGeoZoneNotification = false;
  geoZoneLaunchResult: GeoZoneLaunchNotificationJobAcceptedDto | null = null;

  // Geo-zone launch send log properties
  showSendLogPanel = false;
  sendLogResponse: WhatsAppCampaignSendLogPaginatedResponse | null = null;
  sendLogLoading = false;
  sendLogError = '';
  sendLogStatusFilter = '';
  sendLogCurrentPage = 1;
  sendLogPageSize = 20;
  sendLogTotalPages = 1;
  private sendLogSubscription$ = new Subject<void>();

  loading = false;
  errorMessage = '';
  successMessage = '';

  Math = Math;

  private destroy$ = new Subject<void>();

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadZones();
        this.errorMessage = 'Please select a Geo Zone to load unsupported locations.';
      });
  }

  ngOnDestroy(): void {
    this.locationsSubscription$.next();
    this.locationsSubscription$.complete();
    this.sendLogSubscription$.next();
    this.sendLogSubscription$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadZones(): void {
    this.loadingZones = true;
    this.externalDataService.getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones || [];
          this.loadingZones = false;
        },
        error: (error) => {
          console.error('Error loading zones:', error);
          this.loadingZones = false;
          this.errorMessage = 'Failed to load zones. Please try again.';
        }
      });
  }

  loadLocations(): void {
    if (!this.filters.geoZoneId || !this.filters.geoZoneId.trim()) {
      this.errorMessage = 'Please select a Geo Zone to load unsupported locations.';
      this.loading = false;
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const pageIndex = this.currentPage - 1;
    const geoZoneId = this.filters.geoZoneId.trim();
    const status = this.filters.status?.trim() || '';

    this.locationsSubscription$.next();
    this.locationsSubscription$.complete();
    this.locationsSubscription$ = new Subject<void>();

    this.externalDataService.getUnsupportedLocationsByGeoZone(
      geoZoneId,
      status || undefined,
      pageIndex,
      this.pageSize
    )
      .pipe(takeUntil(this.locationsSubscription$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.locationsResponse = response;
            this.totalCount = response.totalCount || 0;
            this.calculateTotalPages();
          } else {
            this.locationsResponse = null;
            this.totalCount = 0;
            this.totalPages = 1;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading unsupported locations:', error);
          this.loading = false;
          this.locationsResponse = null;

          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view unsupported locations.';
          } else if (error.status === 404) {
            this.errorMessage = 'Unsupported locations not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to load unsupported locations. Please try again.';
          }
        }
      });
  }

  calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  onPageChange(page: number): void {
    if (!this.canLoadLocations()) {
      return;
    }
    this.currentPage = page;
    this.loadLocations();
  }

  onPageSizeChange(size: number): void {
    if (!this.canLoadLocations()) {
      return;
    }
    this.pageSize = size;
    this.currentPage = 1;
    this.loadLocations();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadLocations();
  }

  clearFilters(): void {
    this.filters = {
      geoZoneId: '',
      status: ''
    };
    this.currentPage = 1;
    this.errorMessage = 'Please select a Geo Zone to load unsupported locations.';
    this.locationsSubscription$.next();
    this.locationsSubscription$.complete();
    this.locationsSubscription$ = new Subject<void>();
    this.locationsResponse = null;
    this.totalCount = 0;
    this.totalPages = 1;
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filters.geoZoneId.trim() ||
      this.filters.status.trim()
    );
  }

  canLoadLocations(): boolean {
    return !!(
      this.filters.geoZoneId &&
      this.filters.geoZoneId.trim()
    );
  }

  viewLocationDetails(location: UnsupportedLocationDto): void {
    this.selectedLocation = location;
    this.showDetailsPanel = true;
  }

  closeDetailsPanel(): void {
    this.showDetailsPanel = false;
    this.selectedLocation = null;
  }

  openUpdateStatusModal(location: UnsupportedLocationDto): void {
    this.locationForStatusUpdate = location;
    this.updateStatusForm = {
      status: location.status || '',
      remark: ''
    };
    this.showUpdateStatusModal = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeUpdateStatusModal(): void {
    this.showUpdateStatusModal = false;
    this.locationForStatusUpdate = null;
    this.updateStatusForm = {
      status: '',
      remark: ''
    };
  }

  updateLocationStatus(): void {
    if (!this.locationForStatusUpdate || !this.updateStatusForm.status.trim()) {
      this.errorMessage = 'Status is required.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request: UpdateUnsupportedLocationStatusRequestDto = {
      status: this.updateStatusForm.status.trim(),
      remark: this.updateStatusForm.remark.trim() || undefined
    };

    this.externalDataService.updateUnsupportedLocationStatus(
      this.locationForStatusUpdate.id,
      request
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedLocation) => {
          this.loading = false;
          this.successMessage = 'Location status updated successfully.';
          this.closeUpdateStatusModal();
          this.loadLocations();
          if (this.selectedLocation && this.selectedLocation.id === updatedLocation.id) {
            this.selectedLocation = updatedLocation;
          }
        },
        error: (error) => {
          console.error('Error updating location status:', error);
          this.loading = false;

          if (error.status === 400) {
            this.errorMessage = error.error?.detail || 'Invalid data provided. Please check your input.';
          } else if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to update location status.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to update location status. Please try again.';
          }
        }
      });
  }

  openGeoZoneLaunchModal(): void {
    if (!this.filters.geoZoneId) {
      return;
    }

    const activeZone = this.zones.find(z => z.id === this.filters.geoZoneId);
    this.whatsAppLocationParam = activeZone ? activeZone.name : '';
    this.whatsAppTemplateName = 'location_launch_direct';
    this.whatsAppHeaderImageUrl = '';
    this.geoZoneLaunchResult = null;
    this.errorMessage = '';
    this.successMessage = '';
    this.showGeoZoneLaunchModal = true;
  }

  closeGeoZoneLaunchModal(): void {
    this.showGeoZoneLaunchModal = false;
    this.whatsAppLocationParam = '';
    this.whatsAppHeaderImageUrl = '';
    this.geoZoneLaunchResult = null;
    this.launchingGeoZoneNotification = false;
  }

  launchGeoZoneNotification(): void {
    if (!this.filters.geoZoneId || !this.whatsAppTemplateName.trim()) {
      this.errorMessage = 'GeoZone ID and template name are required.';
      return;
    }

    this.launchingGeoZoneNotification = true;
    this.errorMessage = '';
    this.successMessage = '';

    const bodyParams: string[] = [];
    if (this.whatsAppLocationParam.trim()) {
      bodyParams.push(this.whatsAppLocationParam.trim());
    }

    const request: GeoZoneLaunchNotificationRequestDto = {
      geoZoneId: this.filters.geoZoneId,
      whatsAppTemplateName: this.whatsAppTemplateName.trim(),
      whatsAppBodyParams: bodyParams.length > 0 ? bodyParams : undefined,
      whatsAppHeaderImageUrl: this.whatsAppHeaderImageUrl.trim() || undefined
    };

    this.externalDataService.launchGeoZoneNotification(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.launchingGeoZoneNotification = false;
          this.geoZoneLaunchResult = res;
          this.successMessage = `Notification queued! ${res.queuedCount} message(s) will be sent shortly ` +
            `(${res.matchedRecipientCount} matched, ${res.alreadyNotifiedCount} already notified, ${res.optedOutCount} opted out).`;
        },
        error: (err) => {
          console.error('Error launching geo-zone notification:', err);
          this.launchingGeoZoneNotification = false;
          this.errorMessage = err.error?.detail || 'Failed to launch geo-zone notification. Please try again.';
        }
      });
  }

  openSendLogPanel(): void {
    if (!this.filters.geoZoneId) {
      return;
    }
    this.sendLogStatusFilter = '';
    this.sendLogCurrentPage = 1;
    this.showSendLogPanel = true;
    this.loadSendLog();
  }

  closeSendLogPanel(): void {
    this.showSendLogPanel = false;
    this.sendLogResponse = null;
    this.sendLogError = '';
    this.sendLogSubscription$.next();
    this.sendLogSubscription$.complete();
    this.sendLogSubscription$ = new Subject<void>();
  }

  loadSendLog(): void {
    if (!this.filters.geoZoneId) {
      return;
    }

    this.sendLogLoading = true;
    this.sendLogError = '';

    const pageIndex = this.sendLogCurrentPage - 1;

    this.sendLogSubscription$.next();
    this.sendLogSubscription$.complete();
    this.sendLogSubscription$ = new Subject<void>();

    this.externalDataService.getGeoZoneLaunchSendLog(
      this.filters.geoZoneId,
      this.sendLogStatusFilter || undefined,
      pageIndex,
      this.sendLogPageSize
    )
      .pipe(takeUntil(this.sendLogSubscription$))
      .subscribe({
        next: (response) => {
          this.sendLogResponse = response;
          this.sendLogTotalPages = Math.ceil((response.totalCount || 0) / this.sendLogPageSize) || 1;
          this.sendLogLoading = false;
        },
        error: (error) => {
          console.error('Error loading geo-zone launch send log:', error);
          this.sendLogLoading = false;
          this.sendLogResponse = null;
          this.sendLogError = 'Failed to load send log. Please try again.';
        }
      });
  }

  onSendLogStatusFilterChange(): void {
    this.sendLogCurrentPage = 1;
    this.loadSendLog();
  }

  onSendLogPageChange(page: number): void {
    this.sendLogCurrentPage = page;
    this.loadSendLog();
  }

  onSendLogPageSizeChange(size: number): void {
    this.sendLogPageSize = size;
    this.sendLogCurrentPage = 1;
    this.loadSendLog();
  }

  getStatusBadgeClass(status: string): string {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'converted' || statusLower === 'active') {
      return 'status-badge status-active';
    } else if (statusLower === 'pending') {
      return 'status-badge status-pending';
    } else if (statusLower === 'rejected' || statusLower === 'invalid' || statusLower === 'closed') {
      return 'status-badge status-rejected';
    } else if (statusLower === 'followup' || statusLower === 'follow-up') {
      return 'status-badge status-contacted';
    }
    return 'status-badge status-default';
  }
}


